/* file description */
import {
  useDeepCompareEffect,
  useDebounceFn,
  usePrevious,
  useSetState,
} from 'ahooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import rulesCalc from './rulesCalc';
import _, { cloneDeep, isArray } from 'lodash';
import useMetaStore from './useMetaStore';
import type { Values, Rule, RuleMap, PageRule } from './type';
import { findAssetCascadeData } from '@/api/cascade';
import { getTaskPage } from '@/api/scene';
import { ModuleInfo } from '@/common/model-apis/type';
import { cascadeTransformValues } from '@/asset/AssetTask/service';
import StoreContext from './context';
import { getPageRules } from './utils';
import { RuleTigger } from '@/const';

export interface StoreState {
  getValues: () => Values;
  values?: Values;
  setValues: (values: Record<string, object | object[]>) => void;
  rules?: Rule[];
  setRules: (rules: Rule[]) => void;
  viewValues?: Values;
  setViewValues?: (values: Record<string, object | object[]>) => void;
  ref: {
    current: {
      /** 规则原始数据 */
      rules: Rule[];
      /** 页面使用的数据数据 */
      pageRules: PageRule[];
      /** 规则数据转化为以组件key为关键字的规则map */
      rulesMap: RuleMap;
      /** 经过转化后的可视数据，如详情页的数据 */
      viewValues: any;
      /** 保存values的地址索引，保证在setValuesImmediate中获取到的数据永远是最新的 */
      values: Values;
    };
  };
}

// 此钩子每个组件初始化时执行一次
export const useValues = function (): StoreState {
  const [state, setState] = useSetState<Pick<StoreState, 'values' | 'rules'>>({
    values: undefined,
    rules: [],
  });
  const ref = useRef<any>({
    rules: [],
    pageRules: [],
    rulesMap: {},
    viewValues: {},
    values: {},
    roles: [],
  });
  return {
    getValues: () => ref.current.values,
    values: state.values,
    setValues: (values) => {
      ref.current.values = values;
      setState({ values });
    },
    rules: ref.current.rules,
    setRules: (rules: Rule[]) => {
      const rulesMap: RuleMap = {};
      rules.forEach((rule) => {
        if (rule.RULES) {
          // 解析页面配置中的规则
          if (rule?.TODOOPTIONS?.pageEvent?.isMakeBtn) {
            Object.keys(rule?.TODOOPTIONS?.pageEvent?.btnConfig || {}).forEach(
              (btnConfigType) => {
                Object.keys(
                  _.get(
                    rule,
                    `TODOOPTIONS.pageEvent.btnConfig.${btnConfigType}`,
                    {},
                  ),
                ).forEach((key: string) => {
                  rulesMap[key] = rulesMap[key] || {};
                  if (
                    _.get(rule, [
                      'TODOOPTIONS',
                      'pageEvent',
                      'btnConfig',
                      btnConfigType,
                      key,
                    ])
                  ) {
                    rulesMap[key].visible = rule.RULES;
                  } else {
                    rulesMap[key].hidden = rule.RULES;
                  }
                });
              },
            );
          }
          // 解析模型中的是否可编辑
          (rule?.TODOOPTIONS?.editable || []).forEach((editable) => {
            editable.field.forEach((f) => {
              const key = `${editable.modelCode}.${f}`;
              rulesMap[key] = rulesMap[key] || {};
              rulesMap[key].disabled = rulesMap[key].disabled || [];
              rulesMap[key].disabled!.push(rule.RULES);
            });
          });
          // 解析模型中的是否可编辑
          (rule?.TODOOPTIONS?.mandatory || []).forEach((mandatory) => {
            mandatory.field.forEach((f) => {
              const key = `${mandatory.modelCode}.${f}`;
              rulesMap[key] = rulesMap[key] || {};
              rulesMap[key].required = rule.RULES;
            });
          });
          (rule?.TODOOPTIONS?.entitys || []).forEach((entity) => {
            (entity.attrs || []).forEach((attr) => {
              const key = `${entity.code}.${attr.code}`;
              rulesMap[key] = rulesMap[key] || {};
              if (attr.disabled) {
                rulesMap[key].disabled = rulesMap[key].disabled || [];
                rulesMap[key].disabled!.push(rule.RULES);
              }
              if (attr.required) {
                rulesMap[key].required = rule.RULES;
              }
            });
          });
        }
      });
      ref.current.rulesMap = rulesMap;
      console.log('rulesMap', rulesMap);
      ref.current.rules = rules;
      ref.current.pageRules = getPageRules(rules);
    },
    viewValues: ref.current.viewValues,
    setViewValues: (values) => {
      ref.current.viewValues = values;
    },
    ref,
  };
};

// 此钩子会被创个多个
export default function useValuesStore() {
  const store = useContext(StoreContext);
  const {
    values,
    getValues,
    setValues,
    setRules,
    viewValues,
    setViewValues,
    ref,
  } = store.values;
  const immediateRef = useRef<{
    id?: any;
    cbArray: Array<(v: Values) => Values>;
  }>({ id: undefined, cbArray: [] });
  const {
    setMetaInfo,
    metaInfoByRule,
    moduleList,
    taskId,
    recordId,
    taskInfo,
    myRoles,
  } = useMetaStore();

  // 将数据存储到本地session
  const { run: sessionStorageSet } = useDebounceFn(
    (val: any) => {
      sessionStorage.setItem(`${taskId}-${recordId}`, JSON.stringify(val));
    },
    { wait: 1000 },
  );

  const setValuesWithRule = (v: any, roles?: any[]) => {
    const { pageRules } = ref.current;
    // 执行深度比对，如果数据一致，则不进行设置
    const rules = pageRules.filter(
      (x: PageRule) => x.trigger === RuleTigger.PageValuesChange,
    );
    if (!_.isEqual(values, v)) {
      // 规则执行
      let newValues = _.cloneDeep(v);
      let newMetaInfoByRule;
      try {
        if (
          Object.keys(newValues || {}).length > 0 &&
          Array.isArray(rules) &&
          rules.length > 0 &&
          newValues
        ) {
          const MYROLES = roles || myRoles;
          const res = rulesCalc(
            _.cloneDeep(newValues),
            rules,
            MYROLES,
            taskInfo?.relationTree,
          );
          // setValues(res.values);
          newValues = res.values;
          if (!_.isEqual(metaInfoByRule, res.meta)) {
            newMetaInfoByRule = res.meta;
          }
        }
      } catch (e) {
        console.error('规则执行失败', e);
      }
      console.log('setValue', newValues);
      setValues(newValues);
      if (newMetaInfoByRule) {
        setMetaInfo({ metaInfoByRule: newMetaInfoByRule } as any);
      }
    }
  };

  return {
    getValues,
    values,
    setValues: (moduleID: string, value: any) => {
      setValuesWithRule({
        ...values,
        [moduleID]: value,
      });
    },
    setValuesImmediate: (cb: (v: Values) => Values) => {
      immediateRef.current.cbArray.push(cb);
      clearImmediate(immediateRef.current.id);
      immediateRef.current.id = setImmediate(() => {
        let newValue = _.cloneDeep(ref.current.values || (values as Values));
        while (immediateRef.current.cbArray.length > 0) {
          const next = immediateRef.current.cbArray.shift();
          newValue = next!(newValue);
        }
        newValue && setValuesWithRule(newValue);
      });
    },
    resetValue: (value = {}, myRoles: any[]) => {
      setValuesWithRule({ ...value }, myRoles);
    },
    realoadValue: async (
      moduleId = '',
      sorter?: { field?: string; order?: string },
    ) => {
      if (!taskId || !recordId) return;
      const res = await findAssetCascadeData({
        taskId,
        recordId,
        pageModuleConfig: sorter && { [moduleId]: { sorter } },
      });
      // 获取模板接口
      const taskInfo = await getTaskPage({ taskId });
      const dataSource = cascadeTransformValues(res.response, taskInfo.modules);
      Object.keys(dataSource).forEach((key) => {
        if (isArray(dataSource[key])) return;
        const module = moduleList.find((module) => module.handle === key);
        const emptyVal: Record<string, any> = {};
        module?.fields.forEach((field) => {
          emptyVal[field.name] = undefined;
        });
        dataSource[key] = { ...emptyVal, ...dataSource[key] };
      });

      if (moduleId) {
        setValues({
          ...values,
          [moduleId]: cloneDeep(dataSource[moduleId]),
        });
        return;
      }
      setValues({ ...dataSource });
    },
    rulesMap: ref?.current?.rulesMap,
    pageRules: ref?.current?.pageRules,
    setRules,
    getViewValues: () => {
      return viewValues;
    },
    setViewValuesByKey: (key: string | string[], val: any) => {
      const vv = viewValues || {};
      _.set(vv, key, val);
      sessionStorageSet(vv);
      setViewValues?.(vv);
    },
  };
}

// const getNowValue = async (moduleId: string, recordId: string, module?: ModuleInfo) => {
//   const primaryKey = module?.fields.find((item) => item.isPrimary === true)?.name || '';
//   const resultVal = await getModuleValue({
//     nsId: module?.namespaceHandle || '',
//     moduleId,
//     primaryKey,
//     filterValue: recordId,
//   });
//   return resultVal?.data?.[0];
// };
